/**
 * Main Menu Manager
 * Handles the main menu screen before game starts
 */

import { frontendLogger } from '../../shared/logger';
import type { MapType } from './uiManager';

type Difficulty = 'easy' | 'normal' | 'hard';

export interface MenuSelections {
  map: MapType;
  difficulty: Difficulty;
  timeLimit?: number; // in seconds
}

export class MenuManager {
  private selectedMap: MapType = 'procedural';
  private selectedDifficulty: Difficulty = 'normal';
  private selectedTimeLimit: number = 300; // 5 minutes default
  private onPlayCallback?: (selections: MenuSelections) => void;

  constructor() {
    // Load saved preferences
    const savedMap = localStorage.getItem('selectedMap') as MapType | null;
    if (savedMap === 'procedural' || savedMap === 'fps-arena') {
      this.selectedMap = savedMap;
    }

    const savedDifficulty = localStorage.getItem('selectedDifficulty') as Difficulty | null;
    if (savedDifficulty === 'easy' || savedDifficulty === 'normal' || savedDifficulty === 'hard') {
      this.selectedDifficulty = savedDifficulty;
    }

    this.setupMenu();
    frontendLogger.info('MenuManager initialized', {
      map: this.selectedMap,
      difficulty: this.selectedDifficulty
    });
  }

  private setupMenu(): void {
    this.renderMapOptions();
    this.renderDifficultyOptions();
    this.setupPlayButton();
  }

  private renderMapOptions(): void {
    const container = document.getElementById('menu-maps');
    if (!container) return;

    const maps: Array<{ id: MapType; label: string }> = [
      { id: 'procedural', label: 'Random Terrain (Generated)' },
      { id: 'fps-arena', label: 'FPS Arena Map' } // Your GLB map!
    ];

    maps.forEach(map => {
      const option = document.createElement('div');
      option.className = 'menu-option';
      option.textContent = map.label;
      option.dataset.map = map.id;

      if (map.id === this.selectedMap) {
        option.classList.add('active');
      }

      option.addEventListener('click', () => {
        this.selectMap(map.id);
      });

      container.appendChild(option);
    });
  }

  private renderDifficultyOptions(): void {
    const container = document.getElementById('menu-difficulties');
    if (!container) return;

    const difficulties: Array<{ id: Difficulty; label: string }> = [
      { id: 'easy', label: 'Easy' },
      { id: 'normal', label: 'Normal' },
      { id: 'hard', label: 'Hard' }
    ];

    difficulties.forEach(diff => {
      const option = document.createElement('div');
      option.className = 'menu-option';
      option.textContent = diff.label;
      option.dataset.difficulty = diff.id;

      if (diff.id === this.selectedDifficulty) {
        option.classList.add('active');
      }

      option.addEventListener('click', () => {
        this.selectDifficulty(diff.id);
      });

      container.appendChild(option);
    });
  }

  private setupPlayButton(): void {
    const playButton = document.getElementById('play-button');
    if (!playButton) return;

    playButton.addEventListener('click', () => {
      this.startGame();
    });
  }

  private selectMap(map: MapType): void {
    this.selectedMap = map;
    localStorage.setItem('selectedMap', map);

    // Update UI
    const options = document.querySelectorAll('[data-map]');
    options.forEach(option => {
      if (option instanceof HTMLElement) {
        const isActive = option.dataset.map === map;
        option.classList.toggle('active', isActive);
      }
    });

    frontendLogger.info('Map selected', { map });
  }

  private selectDifficulty(difficulty: Difficulty): void {
    this.selectedDifficulty = difficulty;
    localStorage.setItem('selectedDifficulty', difficulty);

    // Update UI
    const options = document.querySelectorAll('[data-difficulty]');
    options.forEach(option => {
      if (option instanceof HTMLElement) {
        const isActive = option.dataset.difficulty === difficulty;
        option.classList.toggle('active', isActive);
      }
    });

    frontendLogger.info('Difficulty selected', { difficulty });
  }

  private startGame(): void {
    // Hide menu
    const menu = document.getElementById('main-menu');
    if (menu) {
      menu.classList.add('hidden');
    }

    // Show canvas
    const canvas = document.getElementById('c');
    if (canvas) {
      canvas.classList.add('active');
    }

    frontendLogger.info('Starting game', {
      map: this.selectedMap,
      difficulty: this.selectedDifficulty
    });

    // Call the play callback
    if (this.onPlayCallback) {
      this.onPlayCallback({
        map: this.selectedMap,
        difficulty: this.selectedDifficulty,
        timeLimit: this.selectedTimeLimit
      });
    }
  }

  public onPlay(callback: (selections: MenuSelections) => void): void {
    this.onPlayCallback = callback;
  }

  public getSelections(): MenuSelections {
    return {
      map: this.selectedMap,
      difficulty: this.selectedDifficulty,
      timeLimit: this.selectedTimeLimit
    };
  }

  public showMenu(): void {
    const menu = document.getElementById('main-menu');
    const canvas = document.getElementById('c');
    
    if (menu && canvas) {
      menu.classList.remove('hidden');
      canvas.classList.remove('active');
    }
  }

  public hideMenu(): void {
    const menu = document.getElementById('main-menu');
    const canvas = document.getElementById('c');
    
    if (menu && canvas) {
      menu.classList.add('hidden');
      canvas.classList.add('active');
    }
  }

  public destroy(): void {
    // Cleanup if needed in the future
  }
}
